import { createPortal } from "react-dom";
import { FC, useEffect, useRef, useState } from "react";
import EmojiPickerReact, { EmojiClickData } from "emoji-picker-react";

// Styles
import { FaceActivated } from "@carbon/icons-react";

// Components
import { Box } from "@mui/material";


interface _EmojiPickerProps {
  onSelect: (emoji: string) => void;
}

const EmojiPicker: FC<_EmojiPickerProps> = ({ onSelect }) => {
  const iconRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  // Measure icon position after DOM update
  useEffect(() => {
    if (emojiPickerOpen && iconRef.current) {
      requestAnimationFrame(() => {
        const icon = iconRef.current!;
        const rect = icon.getBoundingClientRect();
        const pickerWidth = 350;
        const pickerHeight = 400;

        let top = rect.bottom + 8 + window.scrollY;
        let left = rect.left + window.scrollX;

        // Adjust if overflow
        if (left + pickerWidth > window.innerWidth) {
          left = window.innerWidth - pickerWidth - 8;
        }
        if (top + pickerHeight > window.innerHeight) {
          top = rect.top - pickerHeight - 8 + window.scrollY;
        }

        setPosition({ top, left });
      });
    }
  }, [emojiPickerOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node) &&
        iconRef.current && !iconRef.current.contains(event.target as Node)) {
        setEmojiPickerOpen(false);
      }
    };

    if (emojiPickerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [emojiPickerOpen]);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    onSelect(emojiData.emoji);
    setEmojiPickerOpen(false);
  };

  return (
    <>
      <Box ref={iconRef} display="flex" alignItems="center">
        <FaceActivated
          size="20px"
          onClick={() => {
            setPosition(null);
            setEmojiPickerOpen(true);
          }}
          style={{
            cursor: "pointer",
            color: emojiPickerOpen ? "var(--focus)" : "var(--text-secondary)",
            marginTop: "16px",
          }}
        />
      </Box>

      {emojiPickerOpen &&
        createPortal(
          <Box
            ref={pickerRef}
            style={{
              position: "absolute",
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
              opacity: position ? 1 : 0,
              transition: "opacity 0.1s ease",
              zIndex: 9999,
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            <EmojiPickerReact
              open
              onEmojiClick={handleEmojiClick}
              autoFocusSearch={false}
            />
          </Box>,
          document.body
        )}
    </>
  );
};

export default EmojiPicker;